import React, { useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  PhotoIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  DEFAULT_SCRAPER_COUNTRY_WHITELIST,
  SCRAPER_ACTIVE_PERIOD_OPTIONS,
  RECOMMENDED_AUTO_SOURCE_GATES,
  formatAutoSourceBand,
} from '../constants/scraperDefaults';

import AccountPicker from './ScraperV2AccountPicker';

export default function ScraperV2CreateSessionModal({
  open,
  onClose,
  onSubmit,
  submitting,
  accounts,
  checkers,
  canUseActiveCheckers,
}) {
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [usernames, setUsernames] = useState('');
  const [followerPageCount, setFollowerPageCount] = useState(200);
  const [accountIds, setAccountIds] = useState([]);
  const [checkerAccountIds, setCheckerAccountIds] = useState([]);
  const [filters, setFilters] = useState({
    filterAvatar: false,
    filterDMs: false,
    filterVerified: false,
    filterActiveLastMonth: false,
    filterCountryWhitelist: false,
    autoSourceAutoApprove: false,
    ...RECOMMENDED_AUTO_SOURCE_GATES,
  });
  const [allowedCountriesList, setAllowedCountriesList] = useState(
    () => [...DEFAULT_SCRAPER_COUNTRY_WHITELIST]
  );
  const [countryAddInput, setCountryAddInput] = useState('');
  const [useBlacklist, setUseBlacklist] = useState(false);
  const [activeWithinDays, setActiveWithinDays] = useState(30);
  const [cleanSheet, setCleanSheet] = useState(false);

  if (!open) return null;

  const addCountry = () => {
    const t = countryAddInput.trim();
    if (!t) return;
    const exists = allowedCountriesList.some(
      (c) => c.toLowerCase() === t.toLowerCase()
    );
    if (!exists) {
      setAllowedCountriesList([...allowedCountriesList, t]);
    }
    setCountryAddInput('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      tag: tag.trim(),
      cleanSheet,
      usernames: usernames.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean),
      followerPageCount,
      accountIds,
      activeCheckerAccountIds: checkerAccountIds,
      filters: {
        ...filters,
        allowedCountries: filters.filterCountryWhitelist ? allowedCountriesList : [],
        useBlacklist,
        activeWithinDays,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          New Scraper V2 session
        </h2>

        <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={cleanSheet}
            onChange={(e) => setCleanSheet(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium">Clean sheet (isolated database)</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Starts at 0 raw / 0 stored with its own follow ledger. Current data stays on the legacy pile.
            </span>
          </span>
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <input
            required
            placeholder="Task name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
          />
          <input
            required
            placeholder="Category (e.g. mixed, Gaming)"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
          />
        </div>

        <textarea
          required={!cleanSheet}
          placeholder={cleanSheet
            ? 'Source usernames (optional — you can feed this sheet from follow-backs)'
            : 'Source usernames (one per line)'}
          rows={4}
          value={usernames}
          onChange={(e) => setUsernames(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 dark:bg-gray-800 dark:border-gray-700 font-mono text-sm"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Followers per page (max 200)
          </label>
          <input
            type="number"
            min={1}
            max={200}
            value={followerPageCount}
            onChange={(e) => setFollowerPageCount(Number(e.target.value) || 200)}
            className="w-32 rounded-lg border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
          />
        </div>

        <AccountPicker
          title="Scrape accounts"
          accounts={accounts}
          selectedIds={accountIds}
          onChange={setAccountIds}
          required
          selectableFilter={(acc) => acc.hasValidSession !== false}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Auto-source account gates
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Applied when this session discovers sources from keywords or For You — not to
            usernames you paste above. Recommended: 3,000–29,999 followers, drop Blue,
            link in bio on For You.
          </p>
          <div className="mb-2">
            <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200">
              {formatAutoSourceBand(filters)}
              {filters.autoSourceAutoApprove ? ' · auto-approve FYP' : ' · approve FYP finds'}
            </span>
          </div>
          <div className="flex flex-wrap items-end gap-3 text-sm">
            <label className="text-gray-600 dark:text-gray-400">
              Min followers
              <input
                type="number"
                min={0}
                value={filters.autoSourceMinFollowers}
                onChange={(e) => setFilters({
                  ...filters,
                  autoSourceMinFollowers: Number(e.target.value) || 0,
                })}
                className="mt-0.5 block w-28 rounded-md border px-2 py-1.5 dark:bg-gray-800 dark:border-gray-700"
              />
            </label>
            <label className="text-gray-600 dark:text-gray-400">
              Max followers
              <input
                type="number"
                min={0}
                value={filters.autoSourceMaxFollowers}
                onChange={(e) => setFilters({
                  ...filters,
                  autoSourceMaxFollowers: Number(e.target.value) || 0,
                })}
                className="mt-0.5 block w-28 rounded-md border px-2 py-1.5 dark:bg-gray-800 dark:border-gray-700"
              />
            </label>
            <label className="flex items-center gap-2 pb-1.5">
              <input
                type="checkbox"
                checked={filters.autoSourceRejectBlue}
                onChange={(e) => setFilters({
                  ...filters,
                  autoSourceRejectBlue: e.target.checked,
                })}
              />
              Reject Blue
            </label>
            <label className="flex items-center gap-2 pb-1.5">
              <input
                type="checkbox"
                checked={filters.autoSourceAutoApprove}
                onChange={(e) => setFilters({
                  ...filters,
                  autoSourceAutoApprove: e.target.checked,
                })}
              />
              Auto-approve every FYP find
            </label>
            <label className="flex items-center gap-2 pb-1.5">
              <input
                type="checkbox"
                checked={filters.autoSourceRequireLinks !== false}
                onChange={(e) => setFilters({
                  ...filters,
                  autoSourceRequireLinks: e.target.checked,
                })}
              />
              Require link in bio (FYP)
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Filters (only matching followers will be stored)
          </label>
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.filterAvatar}
                onChange={(e) => setFilters({ ...filters, filterAvatar: e.target.checked })}
              />
              <PhotoIcon className="w-4 h-4" />
              Only store users with profile picture
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.filterDMs}
                onChange={(e) => setFilters({ ...filters, filterDMs: e.target.checked })}
              />
              <ChatBubbleLeftRightIcon className="w-4 h-4" />
              Only store users who accept DMs
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.filterVerified}
                onChange={(e) => setFilters({ ...filters, filterVerified: e.target.checked })}
              />
              <ShieldCheckIcon className="w-4 h-4" />
              Only store verified users
            </label>
            {canUseActiveCheckers && (
              <>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.filterActiveLastMonth}
                    onChange={(e) => setFilters({
                      ...filters,
                      filterActiveLastMonth: e.target.checked,
                    })}
                  />
                  <ClockIcon className="w-4 h-4" />
                  Only store users active within selected period
                </label>
                <p className="pl-6 text-xs text-gray-500 dark:text-gray-400">
                  Activity is always checked and stored. This only decides whether inactive users are kept in the pool.
                </p>
                {filters.filterActiveLastMonth && (
                  <div className="pl-6">
                    <select
                      value={activeWithinDays}
                      onChange={(e) => setActiveWithinDays(Number(e.target.value))}
                      className="rounded-md border px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700"
                    >
                      {SCRAPER_ACTIVE_PERIOD_OPTIONS.map((opt) => (
                        <option key={opt.days} value={opt.days}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.filterCountryWhitelist}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setFilters({ ...filters, filterCountryWhitelist: on });
                      if (on && allowedCountriesList.length === 0) {
                        setAllowedCountriesList([...DEFAULT_SCRAPER_COUNTRY_WHITELIST]);
                      }
                    }}
                  />
                  <UserGroupIcon className="w-4 h-4" />
                  Only store users whose &quot;Based in&quot; country is in your whitelist (About tab)
                </label>
                <p className="pl-6 text-xs text-gray-500 dark:text-gray-400">
                  Country is always fetched and stored. This only decides whether non-whitelisted users are kept in the pool.
                </p>
              </>
            )}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={useBlacklist}
                onChange={(e) => setUseBlacklist(e.target.checked)}
              />
              Use name blacklist (skip accounts with blacklisted names)
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Note: Only followers matching the selected filters will be stored in the database.
            {useBlacklist && ' Accounts with blacklisted names in username/screen name will be skipped immediately.'}
          </p>
        </div>

        {filters.filterCountryWhitelist && canUseActiveCheckers && (
          <div className="pl-4 border-l-2 border-indigo-200 dark:border-indigo-800 space-y-2">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Country names must match X&apos;s About label for spelling; comparison is case-insensitive.
              Your active checker accounts run these checks in a background queue.
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-md bg-gray-50 dark:bg-gray-800/80 border dark:border-gray-700">
              {allowedCountriesList.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200"
                >
                  {c}
                  <button
                    type="button"
                    onClick={() => setAllowedCountriesList(
                      allowedCountriesList.filter((x) => x !== c)
                    )}
                    className="hover:text-red-600 font-semibold"
                    aria-label={`Remove ${c}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={countryAddInput}
                onChange={(e) => setCountryAddInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCountry();
                  }
                }}
                placeholder="Add country (spelling as on X; any case)"
                className="flex-1 min-w-[200px] rounded-md border px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700"
              />
              <button
                type="button"
                onClick={addCountry}
                className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setAllowedCountriesList([...DEFAULT_SCRAPER_COUNTRY_WHITELIST])}
                className="px-3 py-1.5 text-sm rounded-md border dark:border-gray-600"
              >
                Reset default list
              </button>
            </div>
          </div>
        )}

        {canUseActiveCheckers ? (
          <AccountPicker
            title="Checker accounts"
            accounts={checkers}
            selectedIds={checkerAccountIds}
            onChange={setCheckerAccountIds}
            required
          />
        ) : (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Checker accounts are required on every session so country and activity signals can be stored.
          </p>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border dark:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !accountIds.length || !checkerAccountIds.length}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50 hover:bg-indigo-700"
          >
            {submitting ? 'Creating…' : 'Start session'}
          </button>
        </div>
      </form>
    </div>
  );
}
